/**
 * DDSAttendanceModal Component
 * Modal showing attendance/participation data for a DDS
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Users, RefreshCw, CheckCircle, Eye, Clock, 
  Download, FileText 
} from 'lucide-react';

const DDSAttendanceModal = ({ 
  open, 
  onOpenChange, 
  attendanceData, 
  loading,
  onExportPDF
}) => {
  if (!attendanceData && !loading) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const calculateProgress = () => {
    if (!attendanceData?.views?.length || !attendanceData?.total_enviados) return 0;
    return Math.round((attendanceData.views.length / attendanceData.total_enviados) * 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-green-600" />
            Lista de Presença - {attendanceData?.dds?.titulo}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : attendanceData && (
          <div className="space-y-4">
            {/* DDS Info */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-xs text-slate-500">Enviados</p>
                  <p className="text-xl font-bold text-blue-600">
                    {attendanceData.total_enviados || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Visualizados</p>
                  <p className="text-xl font-bold text-green-600">
                    {attendanceData.views?.length || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Pendentes</p>
                  <p className="text-xl font-bold text-amber-600">
                    {(attendanceData.total_enviados || 0) - (attendanceData.views?.length || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Taxa</p>
                  <p className="text-xl font-bold text-purple-600">
                    {calculateProgress()}%
                  </p>
                </div>
              </div>
              
              <div className="mt-3">
                <Progress value={calculateProgress()} className="h-2" />
              </div>
            </div>
            
            {/* Views List */}
            {attendanceData.views?.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 dark:bg-slate-700 px-4 py-2 flex justify-between items-center">
                  <span className="text-sm font-medium">Funcionários que visualizaram</span>
                  {onExportPDF && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={onExportPDF}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  )}
                </div>
                <div className="divide-y max-h-60 overflow-y-auto">
                  {attendanceData.views.map((view, idx) => (
                    <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {view.employee_name || 'Funcionário'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {view.employee_cargo || view.cargo || 'Cargo não informado'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          <Eye className="w-3 h-3 mr-1" />
                          Visualizado
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatDate(view.viewed_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Nenhuma visualização registrada ainda</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DDSAttendanceModal;
